import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  BellNotifications16,
  Calendar,
  ChevronRight,
  Flag,
  TagEmpty16,
  Target,
  Warning,
} from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { Task } from 'shared/types/task';
import { getPlatformShortcut } from 'shared/utils/get-platform-shortcut';

import { MenuGroups, SubMenu } from './types';

export type EditListProps = {
  tasks: Task[];
  openSubMenu: (subMenu: SubMenu) => void;
  registerItem?: (
    group: MenuGroups,
    index: number,
    element: HTMLButtonElement | null,
  ) => void;
  registerGroup?: MenuGroups;
};

export const EditList: React.FC<EditListProps> = ({
  openSubMenu,
  registerItem,
  registerGroup,
}) => {
  const { t } = useTranslation();
  const updatePriority = () => openSubMenu(SubMenu.Priority);
  const updateLinkedItem = () => openSubMenu(SubMenu.LinkedItem);
  const updateDeadlineDeadline = () => openSubMenu(SubMenu.DeadlineDeadline);
  const updateReminderTime = () => openSubMenu(SubMenu.ReminderTime);
  const updateDate = () => openSubMenu(SubMenu.Date);
  const updateTags = () => openSubMenu(SubMenu.Tags);

  return (
    <PopupMenuList hasBottomBorder>
      <PopupMenuListItem>
        <PopupMenuButton
          onClick={updateLinkedItem}
          start={<Icon icon={Target} />}
          end={<Icon icon={ChevronRight} />}
          endColor={Colors.Grey600}
          shortcut={['G']}
          ref={(el) => registerGroup && registerItem?.(registerGroup, 0, el)}
        >
          {t('context-menus.task.multi-select.options.linked-item.label')}
        </PopupMenuButton>
      </PopupMenuListItem>
      <PopupMenuListItem>
        <PopupMenuButton
          onClick={updateDate}
          start={<Icon icon={Calendar} />}
          end={<Icon icon={ChevronRight} />}
          endColor={Colors.Grey600}
          shortcut={['D']}
          ref={(el) => registerGroup && registerItem?.(registerGroup, 1, el)}
        >
          {t('context-menus.task.multi-select.options.date.label')}
        </PopupMenuButton>
      </PopupMenuListItem>
      <PopupMenuListItem>
        <PopupMenuButton
          onClick={updatePriority}
          start={<Icon icon={Warning} />}
          end={<Icon icon={ChevronRight} />}
          endColor={Colors.Grey600}
          shortcut={['P']}
          ref={(el) => registerGroup && registerItem?.(registerGroup, 2, el)}
        >
          {t('context-menus.task.multi-select.options.priority.label')}
        </PopupMenuButton>
      </PopupMenuListItem>

      <PopupMenuListItem>
        <PopupMenuButton
          onClick={updateDeadlineDeadline}
          start={<Icon icon={Flag} />}
          end={<Icon icon={ChevronRight} />}
          endColor={Colors.Grey600}
          shortcut={getPlatformShortcut(['Shift', 'D'])}
          ref={(el) => registerGroup && registerItem?.(registerGroup, 3, el)}
        >
          {t('context-menus.task.multi-select.options.deadline-deadline.label')}
        </PopupMenuButton>
      </PopupMenuListItem>

      <PopupMenuListItem>
        <PopupMenuButton
          onClick={updateTags}
          start={<Icon icon={TagEmpty16} />}
          end={<Icon icon={ChevronRight} />}
          endColor={Colors.Grey600}
          shortcut={['T']}
          ref={(el) => registerGroup && registerItem?.(registerGroup, 4, el)}
        >
          {t('context-menus.task.multi-select.options.tags.label')}
        </PopupMenuButton>
      </PopupMenuListItem>

      <PopupMenuListItem>
        <PopupMenuButton
          onClick={updateReminderTime}
          start={<Icon icon={BellNotifications16} />}
          end={<Icon icon={ChevronRight} />}
          endColor={Colors.Grey600}
          shortcut={['R']}
          ref={(el) => registerGroup && registerItem?.(registerGroup, 5, el)}
        >
          {t('context-menus.task.multi-select.options.reminder-time.label')}
        </PopupMenuButton>
      </PopupMenuListItem>
    </PopupMenuList>
  );
};
